import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from 'lucide-react'
import { Alert } from '@/components/ui/Alert'
import { Modal } from '@/components/ui/Modal'
import { Button, FileInput } from '@/components/ui'
import { useImportStripePayouts } from '@/hooks/useBankReconciliation'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores'

interface UploadStripePayoutModalProps {
  isOpen: boolean
  onClose: () => void
}

export function UploadStripePayoutModal({
  isOpen,
  onClose,
}: UploadStripePayoutModalProps) {
  const { t } = useTranslation('bank_reconciliation')
  const { t: tc } = useTranslation('common')
  const importStripe = useImportStripePayouts()
  const addToast = useNotificationStore((s) => s.addToast)
  // stripe_payout_journal_failed (and any other code this modal doesn't
  // special-case) is translated from the common namespace, where its
  // message lives (common:errors.stripe_payout_journal_failed).
  const translateError = useApiErrorTranslator('common')

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const handleClose = () => {
    if (importStripe.isPending) return
    setSelectedFile(null)
    setFormError(null)
    importStripe.reset()
    onClose()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormError(null)
    importStripe.reset()
    setSelectedFile(e.target.files?.[0] ?? null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFile) {
      setFormError(tc('errors.required'))
      return
    }
    setFormError(null)
    try {
      const result = await importStripe.mutateAsync({ file: selectedFile })
      // The counts ride the toast so the import still confirms what it did; the
      // per-payout breakdown used to sit in a modal the operator had to dismiss
      // before reaching the rows where the work actually happens.
      addToast({
        type: 'success',
        message: t('stripe_payout.import_success_summary', {
          imported: result.meta.imported_count,
          matched: result.meta.match_summary.matched,
        }),
      })
      handleClose()
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { error?: { code?: string; message?: string } } }
      }
      const errorData = error?.response?.data?.error
      if (errorData?.code === 'empty_file') {
        setFormError(t('errors.empty_file'))
      } else if (errorData?.code === 'parse_error') {
        setFormError(errorData.message || t('stripe_payout.import_error'))
      } else {
        // Routes stripe_payout_journal_failed (and anything else uncaught
        // above) through the same translator the mutation-error backstop
        // uses, so this inline alert and any would-be toast agree on one
        // localized message instead of showing the server's raw English text.
        setFormError(translateError(err, { fallback: t('stripe_payout.import_error') }))
      }
    }
  }

  const footer = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={importStripe.isPending}>
        {tc('actions.cancel')}
      </Button>
      <Button
        type="submit"
        form="upload-stripe-payout-form"
        loading={importStripe.isPending}
        leftIcon={<Upload className="w-4 h-4" />}
      >
        {t('stripe_payout.upload_action')}
      </Button>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('stripe_payout.title')}
      footer={footer}
      size="lg"
    >
        <form id="upload-stripe-payout-form" onSubmit={handleSubmit} className="space-y-5">
          {formError && (
            <Alert variant="error" icon={null}>
              <span>{formError}</span>
            </Alert>
          )}

          <p className="text-sm text-base-content/70">{t('stripe_payout.description')}</p>

          <div>
            <label className="block text-sm font-medium mb-1">
              {t('stripe_payout.upload_label')}
            </label>
            <FileInput
              accept=".csv"
              aria-label={t('stripe_payout.upload_label')}
              onChange={handleFileChange}
            />
            <p className="mt-1 text-xs text-base-content/60">
              {t('stripe_payout.upload_hint')}
            </p>
            {formError && !selectedFile && (
              <p className="mt-1 text-sm text-error">{formError}</p>
            )}
          </div>
      </form>
    </Modal>
  )
}
